// ===== 攪拌実施タブ(サブタブ = 1 攪拌 1 パネル) =====

import { useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Stack, Typography, Box, Button, Chip, Table, TableHead, TableBody, TableRow, TableCell,
  FormControlLabel, Switch, Link as MuiLink, Dialog, DialogTitle, DialogContent, DialogActions, Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import QrCodeIcon from "@mui/icons-material/QrCode";
import { PageContext } from "../PageContext";
import { loadDbSlice } from "../../../api/dbSlice";
import { ExtendedDB } from "../../../types/extendedDB";
import { QRCodeInput } from "../../../components/input/QRCodeInput";
import { materialSearchUrl, rangesFromIds } from "../../../util/Materialperser";
import useInput from "../../../util/useInput";
import { errorMessageOf } from "../../../util/typed";
import { plannedMainQtyForWorkDetail } from "../../../util/areaStatus";
import { FULL_EPS, getQrRemainingRatio } from "../../../util/consumption";
import type { ProcessExecutionBase } from "../../../schema/generated/ProcessExecution";
import type { StirBatchBase } from "../../../schema/generated/StirBatch";
import type { ConstructionMaterialConsumptionBase } from "../../../schema/generated/ConstructionMaterialConsumption";
import { DataLink } from "../../../components/links/EntityLinks";
import { computeElapsedSec, mixInfoForWorkDetail, formatDate, PauseMap } from "./helpers";
import { SectionCard, StepStatus } from "./SectionCard";
import { TimerControl } from "./TimerControl";
import { PeSelectField, RemarksField } from "./SessionFields";
import { ConsumptionEntryList } from "./ConsumptionEntryList";

export function StirBatchTab({
  pageContext, db, pes, stirs, consumptions, activeStirId, setActiveStirId,
  hasStir, setHasStir, stirStatus, pauseMap, setPauseMap, now,
  ensureConstruction, reload, setSnackbar,
}: {
  pageContext: PageContext; db: ExtendedDB;
  pes: ProcessExecutionBase[]; stirs: StirBatchBase[];
  consumptions: ConstructionMaterialConsumptionBase[];
  activeStirId: string | null; setActiveStirId: (v: string | null) => void;
  hasStir: boolean; setHasStir: (v: boolean) => void;
  stirStatus: StepStatus;
  pauseMap: PauseMap; setPauseMap: (updater: (prev: PauseMap) => PauseMap) => void;
  now: number;
  ensureConstruction: (planOverride?: string) => Promise<string>;
  reload: () => Promise<unknown> | void;
  setSnackbar: (s: string) => void;
}) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);
  const [qrTargetBatchId, setQrTargetBatchId] = useState<string | null>(null);
  const [consumedQrTargetBatchId, setConsumedQrTargetBatchId] = useState<string | null>(null);
  const qrInput = useInput<{ qrids: number[] }>({ qrids: [] }, () => {});

  // 攪拌対象 = 配合比(実材料)を持つ工程の工程実施のみ。
  const stirrablePes = useMemo(
    () => pes.filter((p) => mixInfoForWorkDetail(db, p.work_detail_id) != null),
    [pes, db]
  );

  const activeStir = useMemo(() => {
    if (activeStirId == null || activeStirId === "new") {
      return null;
    }
    return stirs.find((s) => s.id === activeStirId) ?? null;
  }, [stirs, activeStirId]);

  const run = async (fn: () => Promise<void>, errLabel: string) => {
    if (lock.current) return;
    lock.current = true;
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      pageContext.api.showError(`${errLabel}: ${errorMessageOf(e)}`);
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };

  // 攪拌の新規作成: 工程実施を選んだ時点で行を作る(タイマーは未開始)。配合比は工程から導出。
  const createStir = (peId: string) => run(async () => {
    const pe = stirrablePes.find((p) => p.id === peId);
    if (!pe) {
      throw new Error("選択した工程実施が見つかりません。再読み込みしてください。");
    }
    const mixInfo = mixInfoForWorkDetail(db, pe.work_detail_id);
    const cid = await ensureConstruction();
    const nextOrder = stirs.length > 0 ? Math.max(...stirs.map((r) => r.sort_order)) + 1 : 1;
    const res = await pageContext.api.entities.putRow("stirBatches", {
      construction_id: cid,
      sort_order: nextOrder,
      process_execution_id: pe.id,
      work_detail_id: pe.work_detail_id,
      mix_ratio_definition_id: mixInfo != null ? mixInfo.mix.id : null,
      started_at: null,
      finished_at: null,
      duration_seconds: null,
      remarks: null,
    });
    setSnackbar("攪拌実施を作成しました");
    await reload();
    if (res?.id != null) {
      setActiveStirId(String(res.id));
    }
  }, "作成に失敗しました");

  const patchStir = (row: StirBatchBase, changes: Partial<Pick<StirBatchBase, "process_execution_id" | "work_detail_id" | "mix_ratio_definition_id" | "started_at" | "finished_at" | "duration_seconds" | "remarks">>) =>
    pageContext.api.entities.patchRow("stirBatches", {
      id: row.id,
      construction_id: row.construction_id,
      sort_order: row.sort_order,
      process_execution_id: row.process_execution_id,
      work_detail_id: row.work_detail_id,
      mix_ratio_definition_id: row.mix_ratio_definition_id,
      started_at: row.started_at,
      finished_at: row.finished_at,
      duration_seconds: row.duration_seconds,
      remarks: row.remarks,
      ...changes,
    });

  const changePe = (row: StirBatchBase, peId: string) => run(async () => {
    const pe = stirrablePes.find((p) => p.id === peId);
    if (!pe) {
      throw new Error("選択した工程実施が見つかりません。");
    }
    const mixInfo = mixInfoForWorkDetail(db, pe.work_detail_id);
    await patchStir(row, { process_execution_id: pe.id, work_detail_id: pe.work_detail_id, mix_ratio_definition_id: mixInfo != null ? mixInfo.mix.id : null });
    await reload();
  }, "更新に失敗しました");

  const startTimer = (row: StirBatchBase) => run(async () => {
    await patchStir(row, { started_at: Date.now() });
    await reload();
  }, "開始に失敗しました");

  // 一時停止時は duration_seconds に経過秒数を patch する(チェックポイント保存)。
  // リロードで pauseMap(クライアントのみ)が揮発しても、再開ロード側でこの値から paused_total_ms を逆算できる。
  const pauseRow = (row: StirBatchBase) => run(async () => {
    const elapsed = computeElapsedSec(row.started_at, null, pauseMap[row.id], Date.now());
    setPauseMap((prev) => {
      const cur = prev[row.id];
      return { ...prev, [row.id]: { pausedTotalMs: cur != null ? cur.pausedTotalMs : 0, pausedAt: Date.now() } };
    });
    await patchStir(row, { duration_seconds: elapsed });
    await reload();
  }, "一時停止に失敗しました");
  const resumeRow = (id: string) => {
    setPauseMap((prev) => {
      const cur = prev[id];
      const add = cur != null && cur.pausedAt != null ? Date.now() - cur.pausedAt : 0;
      return { ...prev, [id]: { pausedTotalMs: (cur != null ? cur.pausedTotalMs : 0) + add, pausedAt: null } };
    });
  };

  const finishTimer = (row: StirBatchBase) => run(async () => {
    const elapsed = computeElapsedSec(row.started_at, null, pauseMap[row.id], Date.now());
    await patchStir(row, { finished_at: Date.now(), duration_seconds: elapsed });
    setPauseMap((prev) => {
      const n = { ...prev };
      delete n[row.id];
      return n;
    });
    setSnackbar("攪拌実施を完了しました");
    await reload();
  }, "完了に失敗しました");

  const saveRemarks = (row: StirBatchBase, remarks: string) => run(async () => {
    await patchStir(row, { remarks: remarks === "" ? null : remarks });
    await reload();
  }, "備考の保存に失敗しました");

  // スキャンQRの検証状態(tri-state)。
  //   valid   = QR個体が存在し、搬入済み(carry_in_id あり)・残量>FULL_EPS・主材料一致
  //   invalid = QR個体は存在するが、明確に不一致(搬入前/残量なし(全量消費済み)/材料不一致)
  //   unknown = materialQrs または lots が未同期で判定不能(存在しない = ローカル未同期の可能性がある)
  // invalid のみ拒否する。unknown を拒否すると現場作業を止める誤ブロックになるため、
  // 再同期してもなお unknown なら API 登録に委ね、クライアントでは拒否しない。
  // materialQrs/lots は呼び出し元から渡す(再同期直後は React state の db が古いままのため、
  // classify 対象は常に呼び出し元が明示的に用意した最新配列を使う)。
  // 残量は cmc(全施工の消費行)の Σ used_ratio から導出する(全量消費済みキャッシュ列は見ない)。
  // 残量ゼロの QR は「使用済QRを読み込む」経由でのみ追加できる(通常スキャンでは invalid)。
  const classifyQrForBatch = (
    qrId: number,
    mainMaterialId: number | null,
    materialQrs: ExtendedDB["materialQrs"],
    lots: ExtendedDB["lots"],
    cmc: ExtendedDB["constructionMaterialConsumptions"],
  ): "valid" | "invalid" | "unknown" => {
    const qr = materialQrs.find((q) => q.id === qrId);
    if (qr == null) {
      return "unknown";
    }
    if (qr.carry_in_id == null) {
      return "invalid";
    }
    if (getQrRemainingRatio(cmc, qrId) <= FULL_EPS) {
      return "invalid";
    }
    if (mainMaterialId == null) {
      return "unknown";
    }
    const lot = qr.lot_id != null ? lots.find((l) => l.id === qr.lot_id && l.deleted_at == null) : null;
    if (lot == null) {
      return "unknown";
    }
    if (lot.material_id !== mainMaterialId) {
      return "invalid";
    }
    return "valid";
  };

  const consumeQrForBatch = (batchId: string, ids: number[]) => run(async () => {
    setQrTargetBatchId(null);
    const batch = stirs.find((s) => s.id === batchId);
    const mixInfo = mixInfoForWorkDetail(db, batch?.work_detail_id ?? null);
    const mainMaterialId = mixInfo?.mainMaterial?.id ?? null;
    // 同一バッチ内の重複読取のみ拒否する(別バッチ・別施工での再スキャンは残量が有れば許可)。
    const batchConsumedSet = new Set(consumptions.filter((c) => c.stir_batch_id === batchId).map((c) => c.material_qr_id));
    const newIds = Array.from(new Set(ids)).filter((id) => !batchConsumedSet.has(id));
    if (newIds.length === 0) {
      setSnackbar("このバッチで既に読み取り済みのQRです(使用量はエントリリストで編集してください)");
      return;
    }

    let materialQrs = db.materialQrs;
    let lots = db.lots;
    const cmc = db.constructionMaterialConsumptions;
    let classified = newIds.map((id) => ({ id, state: classifyQrForBatch(id, mainMaterialId, materialQrs, lots, cmc) }));
    const unknownIds = classified.filter((c) => c.state === "unknown").map((c) => c.id);
    if (unknownIds.length > 0 && !pageContext.api.isOnline) {
      // オフライン時はサーバーへ再取得できず、登録APIによる検証にも委ねられない
      // (putRowがIndexedDBへ直接フォールバックするため無条件で登録されてしまう)。
      // 不明QRの盲目的なオフライン登録は行わず、ここで明示的に拒否する。
      pageContext.api.showError(`スキャンしたQRのうち ${unknownIds.length} 件がローカルに存在せず、オフラインのため確認できません。オンラインに接続してから再度お試しください。`);
      return;
    }
    if (unknownIds.length > 0 && pageContext.api.isOnline) {
      // 未同期の可能性があるので、対象QRのみサーバーから再取得して再判定する。
      // reload() はキャッシュ再取得を予約するだけで、IndexedDB への反映完了を待たない
      // (react state の db は次のレンダーまで古いまま)。ここでは同じ IndexedDB スライスを
      // 直接読み直し、その場で得た最新配列で再分類する。
      try {
        await pageContext.api.material.getFromIdSearch({ targetRanges: rangesFromIds(unknownIds) });
        const refreshed = await loadDbSlice(pageContext.api.indexedDB, pageContext.api.currentUser.id, ["materialQrs", "lots"]);
        materialQrs = refreshed.materialQrs ?? materialQrs;
        lots = refreshed.lots ?? lots;
        reload();
      } catch (e) {
        // 再同期に失敗しても unknown のまま扱う(下の分岐で拒否せず API 登録へ委ねる)。
      }
      classified = newIds.map((id) => ({ id, state: classifyQrForBatch(id, mainMaterialId, materialQrs, lots, cmc) }));
    }

    const invalidIds = classified.filter((c) => c.state === "invalid").map((c) => c.id);
    if (invalidIds.length > 0) {
      pageContext.api.showError(`スキャンしたQRのうち ${invalidIds.length} 件が消費できません(搬入未済・残量なし・主材料不一致のいずれか)。全量消費済みの袋は「使用済QRを読み込む」から追加してください。`);
      return;
    }

    // unknown が残っていても、ここでクライアント側で拒否せず登録APIに委ねる(誤ブロック防止)。
    const cid = await ensureConstruction();
    for (const id of newIds) {
      const remaining = getQrRemainingRatio(cmc, id);
      await pageContext.api.entities.putRow("constructionMaterialConsumptions", {
        construction_id: cid,
        material_qr_id: id,
        stir_batch_id: batchId,
        used_ratio: Math.min(1, Math.max(0, remaining)),
      });
    }
    setSnackbar(`${newIds.length} 件のQRを消費しました`);
    await reload();
  }, "QR消費の登録に失敗しました");

  // 使用済QR(残量ゼロ)の紐付けのみ追加。used_ratio=0 で登録し、使用量はエントリリストから編集する。
  const addConsumedQr = (batchId: string, qrId: number) => run(async () => {
    setConsumedQrTargetBatchId(null);
    const cid = await ensureConstruction();
    await pageContext.api.entities.putRow("constructionMaterialConsumptions", {
      construction_id: cid,
      material_qr_id: qrId,
      stir_batch_id: batchId,
      used_ratio: 0,
    });
    setSnackbar("使用済QRを読み込みました(0kg・紐付けのみ)");
    await reload();
  }, "使用済QRの読み込みに失敗しました");

  // エントリの使用量パッチ・削除(生の書き込みのみ。呼び出し元(ConsumptionEntryList)が run()+reload で包む)。
  const patchConsumptionRatio = (row: ConstructionMaterialConsumptionBase, newRatio: number) =>
    pageContext.api.entities.patchRow("constructionMaterialConsumptions", {
      id: row.id,
      construction_id: row.construction_id,
      material_qr_id: row.material_qr_id,
      stir_batch_id: row.stir_batch_id,
      used_ratio: newRatio,
    });

  const deleteConsumptionEntry = (id: string) =>
    pageContext.api.entities.setDelete("constructionMaterialConsumptions", id, true);

  const del = (id: string) => run(async () => {
    // 撹拌実施を削除すると配下の消費行も同時に落とす(材料が消費済みのまま座礁しないように)。
    await pageContext.api.entities.setDeleteWithConsumptionCascade("stirBatches", id, true);
    setSnackbar("攪拌実施を削除しました");
    await reload();
  }, "削除に失敗しました");

  const toggleHasStir = (v: boolean) => {
    setHasStir(v);
  };

  const showPanel = hasStir && (activeStir != null || activeStirId === "new");

  if (!showPanel) {
    const emptyErrors: string[] = [];
    if (hasStir && pes.length === 0) {
      emptyErrors.push("工程実施が無いため作成できません。先に「施工箇所選択」で施工箇所と工程を登録してください");
    }
    return (
      <SectionCard title="攪拌実施" status={stirStatus}
        errors={emptyErrors}
        info={hasStir ? "攪拌を行う場合は「攪拌を作成」、行わない場合は「この施工で攪拌を行う」をオフにしてください。" : "この施工では攪拌(材料を混ぜる作業)を行いません。トグルをオンにすると記録できます。"}>
        <Stack gap={1.5}>
          <FormControlLabel
            control={<Switch checked={hasStir} onChange={(_, v) => toggleHasStir(v)} />}
            label="この施工で攪拌を行う" />
          {hasStir && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
              <Button variant="contained" size="large" startIcon={<AddIcon />} disabled={pes.length === 0}
                onClick={() => setActiveStirId("new")}>攪拌を作成</Button>
            </Box>
          )}
        </Stack>
      </SectionCard>
    );
  }

  // 新規パネル: 工程実施(配合比)の選択のみ。選択と同時に行を作成してそのサブタブへ移る。
  if (activeStir == null) {
    return (
      <SectionCard title="材料準備" status="incomplete"
        errors={["配合比(工程実施)を選択してください"]}
        info="配合比を選ぶと攪拌実施が作成されます。">
        <Stack gap={1.5} alignItems="flex-start">
          <PeSelectField db={db} pes={stirrablePes} value="" busy={busy} onSelect={(peId) => createStir(peId)} withMaterials />
          <Button size="small" onClick={() => setActiveStirId(null)}>キャンセル</Button>
        </Stack>
      </SectionCard>
    );
  }

  const stirRow = activeStir;
  const batchConsumptions = consumptions.filter((c) => c.stir_batch_id === stirRow.id);
  const mixInfo = mixInfoForWorkDetail(db, stirRow.work_detail_id);
  const mainMaterialQrManaged = mixInfo?.mainMaterial?.qr_managed === true;
  // 主材料が QR 管理外なら「手動準備」扱いで完了とみなす(mock isStirBatchComplete 準拠)。
  const materialComplete = activeStir.process_execution_id != null && (!mainMaterialQrManaged || batchConsumptions.length > 0);
  const materialErrors: string[] = [];
  if (activeStir.process_execution_id == null) {
    materialErrors.push("配合比を選択してください");
  } else if (mainMaterialQrManaged && batchConsumptions.length === 0) {
    materialErrors.push("主材料のQRコードを1個以上読み込んでください");
  }
  const timerDone = activeStir.finished_at != null;
  let timerStatus: StepStatus = "incomplete";
  if (timerDone) {
    timerStatus = "done";
  } else if (activeStir.started_at != null) {
    timerStatus = "in_progress";
  }

  // 配合比明細(材料リスト)。計画数量は工程実施の施工箇所の計画材料(材料単位)から合算する。
  const linkedAreaIds = new Set(pes.filter((p) => p.id === stirRow.process_execution_id).map((p) => p.construction_plan_area_id));
  let mainPlannedQty = 0;
  if (mixInfo != null && stirRow.work_detail_id != null) {
    const mainMatId = mixInfo.mix.material_id;
    for (const linkedAreaId of linkedAreaIds) {
      mainPlannedQty += plannedMainQtyForWorkDetail(linkedAreaId, mainMatId, stirRow.work_detail_id, db.constructionPlanAreaPlannedMaterials);
    }
  }
  // 消費QR → 材料ID(lot 経由)。行ごとの消費数・使用量(Σ used_ratio × 内容量)を出すために使う。
  const consumedMaterialIdOf = (qrId: number): number | null => {
    const qr = db.materialQrs.find((q) => q.id === qrId);
    if (qr == null || qr.lot_id == null) {
      return null;
    }
    const lotId = qr.lot_id;
    const lot = db.lots.find((l) => l.id === lotId);
    return lot?.material_id ?? null;
  };
  const matRows: { key: number; materialName: string; detailName: string; ratio: string; planned: number; unit: string; qrManaged: boolean; isMain: boolean; consumedCount: number; usedQty: number | null }[] = [];
  let mainDetailForKg: { content_quantity: number | null } | undefined;
  if (mixInfo != null) {
    const mixDefId = mixInfo.mix.id;
    const mixDetails = db.mixRatioDetails.filter((d) => d.deleted_at == null && d.mix_ratio_definition_id === mixDefId);
    const firstDetail = mixDetails[0];
    const ratioMain = firstDetail?.ratio_start ?? 1;
    for (const d of mixDetails) {
      const md = db.materialDetails.find((x) => x.id === d.material_detail_id);
      const mdMatId = md?.material_id ?? null;
      const matOfRow = mdMatId != null ? db.materials.find((m) => m.id === mdMatId) : null;
      const isMain = firstDetail != null && d.material_detail_id === firstDetail.material_detail_id;
      if (isMain) {
        mainDetailForKg = md;
      }
      let ratioText = d.ratio_start != null ? String(d.ratio_start) : "-";
      if (d.ratio_end != null && d.ratio_end !== d.ratio_start) {
        ratioText = `${d.ratio_start}〜${d.ratio_end}`;
      }
      let planned = mainPlannedQty;
      if (!isMain && ratioMain > 0) {
        planned = mainPlannedQty * (d.ratio_start ?? 1) / ratioMain;
      }
      const rowQrManaged = matOfRow?.qr_managed === true;
      const rowConsumptions = mdMatId == null ? [] : batchConsumptions.filter((c) => consumedMaterialIdOf(c.material_qr_id) === mdMatId);
      const consumedCount = rowConsumptions.length;
      const ratioSum = rowConsumptions.reduce((s, c) => s + c.used_ratio, 0);
      const usedQty = rowQrManaged && md?.content_quantity != null ? ratioSum * md.content_quantity : null;
      matRows.push({
        key: d.id,
        materialName: matOfRow?.name ?? "-",
        detailName: md?.detail_name ?? "-",
        ratio: ratioText,
        planned: planned,
        unit: md?.content_unit ?? "kg",
        qrManaged: rowQrManaged,
        isMain: isMain,
        consumedCount: consumedCount,
        usedQty: usedQty,
      });
    }
  }
  const consumedSerials = batchConsumptions.map((c) => c.material_qr_id);
  const totalUsedRatio = batchConsumptions.reduce((s, c) => s + c.used_ratio, 0);
  const usedMainKg = mainMaterialQrManaged && mainDetailForKg?.content_quantity != null
    ? totalUsedRatio * mainDetailForKg.content_quantity
    : null;
  const zeroUsageWarning = batchConsumptions.length > 0 && totalUsedRatio <= FULL_EPS;
  // 「使用済QRを読み込む」候補: 搬入済・主材料一致・残量≤FULL_EPS・このバッチで未読取。
  const mainMaterialForConsumed = mixInfo?.mainMaterial ?? null;
  const consumedQrCandidates = mainMaterialForConsumed != null
    ? db.materialQrs.filter((q) => {
        if (q.carry_in_id == null) {
          return false;
        }
        if (consumedSerials.includes(q.id)) {
          return false;
        }
        const lot = q.lot_id != null ? db.lots.find((l) => l.id === q.lot_id) : null;
        if (lot?.material_id !== mainMaterialForConsumed.id) {
          return false;
        }
        return getQrRemainingRatio(db.constructionMaterialConsumptions, q.id) <= FULL_EPS;
      }).sort((a, b) => a.id - b.id)
    : [];

  return (
    <Stack gap={2}>
      <Stack direction="row" justifyContent="flex-end">
        <Button size="small" color="error" startIcon={<DeleteIcon />} disabled={busy} onClick={() => del(activeStir.id)}>この攪拌実施を削除</Button>
      </Stack>

      <SectionCard title="材料準備" status={materialComplete ? "done" : "incomplete"} errors={materialErrors}
        info="配合比を選んで、使う材料のQRを読み取ります(QR管理外の材料は手動で準備)。">
        <Stack gap={1.5}>
          <PeSelectField db={db} pes={stirrablePes} value={activeStir.process_execution_id ?? ""} busy={busy}
            onSelect={(peId) => changePe(activeStir, peId)} withMaterials />
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            {mixInfo?.mainMaterial != null && <Chip size="small" variant="outlined" label={`主材料: ${mixInfo.mainMaterial.name}`} sx={{ pointerEvents: "none" }} />}
            {mixInfo != null && mainPlannedQty > 0 && <Chip size="small" variant="outlined" color="primary" label={`主材料 計画 ${mainPlannedQty.toFixed(1)}`} sx={{ pointerEvents: "none" }} />}
            {usedMainKg != null && <Chip size="small" color="primary" label={`主材料 使用 ${usedMainKg.toFixed(1)} kg`} sx={{ pointerEvents: "none" }} />}
            {consumedSerials.length > 0 && (
              <Chip size="small" color="info"
                component={MuiLink}
                clickable
                onClick={() => navigate(materialSearchUrl(consumedSerials))}
                label={`QR ${consumedSerials.length}枚`} />
            )}
            <Box flex={1} />
            <Button size="small" variant="text" sx={{ color: "text.secondary" }} disabled={busy || !mainMaterialQrManaged}
              onClick={() => setConsumedQrTargetBatchId(activeStir.id)}>使用済QRを読み込む</Button>
            <Button size="small" variant="outlined" startIcon={<QrCodeIcon />} disabled={busy || !mainMaterialQrManaged}
              onClick={() => setQrTargetBatchId(activeStir.id)}>QR スキャン</Button>
          </Stack>
          {batchConsumptions.length > 0 && (
            <ConsumptionEntryList
              db={db}
              entries={batchConsumptions}
              allCmc={db.constructionMaterialConsumptions}
              busy={busy}
              run={run}
              reload={reload}
              setSnackbar={setSnackbar}
              patchRatio={patchConsumptionRatio}
              deleteEntry={deleteConsumptionEntry}
            />
          )}
          {zeroUsageWarning && (
            <Alert severity="warning">使用量が0です(消費済QRの紐付けのみ)。このまま保存できます。</Alert>
          )}
          {matRows.length === 0 ? (
            <Typography variant="caption" color="text.secondary">配合比明細が未設定のため材料リストはありません。</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>材料</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>細目</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} align="right">比率</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} align="right">計画数量</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} align="right">使用量</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>QR 管理</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} align="right">QR 数</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {matRows.map((r) => (
                  <TableRow key={r.key} sx={r.isMain ? { backgroundColor: "rgba(25, 118, 210, 0.04)" } : {}}>
                    <TableCell>
                      <Stack direction="row" alignItems="center" gap={0.5}>
                        {r.isMain && <Chip size="small" color="primary" label="主" sx={{ height: 18, fontSize: 10, pointerEvents: "none" }} />}
                        <Typography variant="body2">{r.materialName}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>{r.detailName}</TableCell>
                    <TableCell align="right"><Typography variant="caption">{r.ratio}</Typography></TableCell>
                    <TableCell align="right">{r.planned > 0 ? `${r.planned.toFixed(1)} ${r.unit}` : "-"}</TableCell>
                    <TableCell align="right">
                      {r.qrManaged && r.usedQty != null
                        ? <Typography variant="body2" fontWeight={600}>{r.usedQty.toFixed(1)} {r.unit}</Typography>
                        : <Typography variant="caption" color="text.secondary">手動準備</Typography>}
                    </TableCell>
                    <TableCell>
                      {r.qrManaged ? <Chip size="small" color="primary" label="ON" sx={{ pointerEvents: "none" }} /> : <Chip size="small" label="OFF" sx={{ pointerEvents: "none" }} />}
                    </TableCell>
                    <TableCell align="right">
                      {r.qrManaged ? <Typography variant="body2">{r.consumedCount}</Typography> : <Typography variant="caption" color="text.secondary">-</Typography>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Stack>
      </SectionCard>

      <SectionCard title="攪拌タイマー" status={timerStatus}
        errors={activeStir.process_execution_id == null ? [] : (timerDone ? [] : ["攪拌タイマーを開始して完了してください"])}
        info={activeStir.process_execution_id == null
          ? "攪拌タイマーを使用する前に、配合比を選択してください。"
          : "攪拌タイマーで開始〜完了を計測します。攪拌・作業のタイマーは並行して動かせます(混ぜながら別の箇所を施工できます)。"}>
        {activeStir.process_execution_id != null && (
          <Stack gap={1.5}>
            <TimerControl
              startedAt={activeStir.started_at} finishedAt={activeStir.finished_at} durationSeconds={activeStir.duration_seconds}
              pause={pauseMap[activeStir.id]} now={now} busy={busy}
              onStart={() => startTimer(activeStir)}
              onPause={() => pauseRow(activeStir)} onResume={() => resumeRow(activeStir.id)} onFinish={() => finishTimer(activeStir)}
            />
            <RemarksField key={activeStir.id} initial={activeStir.remarks ?? ""} busy={busy} onSave={(v) => saveRemarks(activeStir, v)} />
          </Stack>
        )}
      </SectionCard>

      <Dialog open={qrTargetBatchId != null} onClose={() => setQrTargetBatchId(null)} fullWidth maxWidth="sm">
        <DialogTitle>QRコード読み取り(材料消費)</DialogTitle>
        <DialogContent dividers>
          {qrTargetBatchId != null && (
            <QRCodeInput
              key={qrTargetBatchId}
              state={qrInput.getState("qrids")}
              onFinished={(ids) => consumeQrForBatch(qrTargetBatchId, ids ?? [])}
              onCancel={() => setQrTargetBatchId(null)}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrTargetBatchId(null)}>閉じる</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={consumedQrTargetBatchId != null} onClose={() => setConsumedQrTargetBatchId(null)} fullWidth maxWidth="sm">
        <DialogTitle>使用済QRを読み込む</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 1.5 }}>
            全量消費済みの袋のQRを読み込みます(読み込み時は0kg=紐付けのみ)。使用量は読取前残量の範囲内でのみ保存できます(残量を超える入力は保存されません)。
          </Alert>
          {consumedQrCandidates.length === 0 ? (
            <Typography variant="body2" color="text.secondary">この主材料の全量消費済みQRはありません。</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>QR</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>LOT</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>消費日</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>消費先施工</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {consumedQrCandidates.map((q) => {
                  const lot = q.lot_id != null ? db.lots.find((l) => l.id === q.lot_id) : null;
                  const latest = db.constructionMaterialConsumptions
                    .filter((c) => c.material_qr_id === q.id && c.deleted_at == null)
                    .map((c) => ({ c, con: db.constructions.find((x) => x.id === c.construction_id) }))
                    .sort((a, b) => (b.con?.executed_at ?? 0) - (a.con?.executed_at ?? 0))[0];
                  return (
                    <TableRow key={q.id} hover>
                      <TableCell>QR#{q.id}</TableCell>
                      <TableCell>{lot?.lot_no ?? (lot ? `LOT#${lot.id.slice(0, 8)}` : "-")}</TableCell>
                      <TableCell>{formatDate(latest?.con?.executed_at)}</TableCell>
                      <TableCell>{latest?.con != null ? <DataLink type="construction" id={latest.con.id} /> : "-"}</TableCell>
                      <TableCell align="right">
                        <Button size="small" variant="outlined" disabled={busy}
                          onClick={() => {
                            if (consumedQrTargetBatchId != null) {
                              addConsumedQr(consumedQrTargetBatchId, q.id);
                            }
                          }}>読み込む</Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConsumedQrTargetBatchId(null)}>閉じる</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
